// Event-firing snippet for browser / JavaScript. Companion module to
// player-base-pairing.js - signs each request with HMAC-SHA256 and is
// a no-op until the player has paired.

export function buildEventWebSnippet(apiKey: string, functionsBaseUrl: string): string {
  return `// player-base-events.js (ES module, served over HTTPS or localhost)
// Companion module to player-base-pairing.js. Import the singleton
// anywhere in your web build:
//
//   import { PlayerBaseEvents } from "./player-base-events.js";
//   PlayerBaseEvents.fire("tutorialComplete");
//   PlayerBaseEvents.fire("bossDefeated", { boss: "stage1", tries: 4 });
//
// Behaviour:
//   * No-op until the player has paired, so it's safe to call from
//     gameplay code without guarding.
//   * Generates a fresh idempotencyKey per fire, so retries collapse
//     server-side instead of double-counting.
//   * 200 covers ok / duplicate / no_active_playtest - all expected
//     during normal operation. Anything else logs a console.warn so
//     config drift in dev builds is visible without crashing gameplay.

import { PlayerBasePairing } from "./player-base-pairing.js";

const API_KEY = "${apiKey}";
const EVENTS_URL = "${functionsBaseUrl}/events";

export const PlayerBaseEvents = {
  async fire(eventName, payload = {}) {
    if (!PlayerBasePairing.isPaired()) return;
    const body = JSON.stringify({
      playerToken: PlayerBasePairing.playerToken,
      eventName,
      idempotencyKey: crypto.randomUUID(),
      payload,
      occurredAt: new Date().toISOString(),
    });
    const signature = await hmacSha256Hex(API_KEY, body);
    try {
      const res = await fetch(EVENTS_URL, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-playerbase-signature": signature,
        },
        body,
      });
      if (!res.ok) {
        console.warn("PlayerBase: " + eventName + " returned " + res.status);
      }
    } catch (err) {
      console.warn("PlayerBase: " + eventName + " failed (transport)", err);
    }
  },
};

async function hmacSha256Hex(key, body) {
  const enc = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, enc.encode(body));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
`;
}
